export const AUDIT_ACTOR_KINDS = [
  "ops",
  "driver",
  "applicant",
  "function",
  "cron",
  "api_key",
  "system",
] as const

export type AuditActorKind = (typeof AUDIT_ACTOR_KINDS)[number]

export const AUDIT_ACTOR_KIND_LABELS: Record<AuditActorKind, string> = {
  ops: "Ops",
  driver: "Driver",
  applicant: "Applicant",
  function: "Edge function",
  cron: "Cron",
  api_key: "API key",
  system: "System",
}

// Embedded through audit_logs_actor_user_id_fkey, so `select` must name it the same way.
export const AUDIT_LOG_SELECT =
  "*, actor_user:users!audit_logs_actor_user_id_fkey(person_id, first_name, last_name)"

export type AuditActorUser = {
  person_id: string | null
  first_name: string | null
  last_name: string | null
}

export type AuditActor = {
  actor: string | null
  actor_user_id: string | null
  actor_kind: string
  actor_user: AuditActorUser | null
}

// Keep in step with buildPersonLabel in supabase/functions/_shared/utils/person-label.ts, so a
// user with no Active person reads like everyone else. An applicant may not have a person_id yet.
export const formatAuditActorUser = ({ person_id, first_name, last_name }: AuditActorUser) => {
  const name = `${first_name ?? ""}${last_name ?? ""}`.replace(/\s+/g, "")
  if (!name) return null
  return person_id ? `${person_id}-${name}` : name
}

// `actor` is null for an applicant and anyone else with no Active person, so their
// users row names them instead. That embed is null when RLS hides users from the reader.
export const auditActorLabel = ({ actor, actor_kind, actor_user }: AuditActor) =>
  actor?.trim() ||
  (actor_user && formatAuditActorUser(actor_user)) ||
  AUDIT_ACTOR_KIND_LABELS[actor_kind as AuditActorKind] ||
  "System"

// Rows from before actor_kind existed all default to system, whoever made them.
export const auditActorKindBadge = ({ actor_kind }: Pick<AuditActor, "actor_kind">) =>
  actor_kind === "system" ? null : (AUDIT_ACTOR_KIND_LABELS[actor_kind as AuditActorKind] ?? null)
